import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { FaBuilding, FaSearch, FaTimes, FaTrash } from 'react-icons/fa';
import api from '../../services/api';
import '../../css/financeiro-parcela-pagar.css';
import { LIMITE_BUSCA_FORNECEDOR, cidadeUfFornecedor, documentoFornecedor, fornecedorDoRegistro, parametrosBuscaFornecedor, rotuloFornecedor, temFornecedor } from './fornecedorRotuloPolicy';

// Busca do fornecedor da linha do orçamento (Rafael 20/10): substitui o select com busca de cada
// linha. Um modal só para a grade inteira — o pai guarda a linha alvo. Em portal no <body>: fora do
// <form> do orçamento (Enter não salva) e livre do sticky/overflow da grade.
const ATRASO_BUSCA_MS = 300;

export default function ModalBuscaFornecedor({ token, fornecedorAtual = null, subtitulo = null, motivoIndisponivel = null, onSelecionar, onRemover = null, onFechar }) {
    const [termo, setTermo] = useState('');
    const [registros, setRegistros] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState(null);
    const [ativo, setAtivo] = useState(-1);
    const [buscaImediata, setBuscaImediata] = useState(0);
    const inputRef = useRef(null);
    const listaRef = useRef(null);

    useEffect(() => {
        if (inputRef.current) inputRef.current.focus();
    }, []);

    // Esc fecha (captura no document: o foco pode estar na tabela, não só na busca).
    useEffect(() => {
        const aoTeclar = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                onFechar();
            }
        };
        document.addEventListener('keydown', aoTeclar, true);
        return () => document.removeEventListener('keydown', aoTeclar, true);
    }, [onFechar]);

    useEffect(() => {
        const controle = new AbortController();
        setCarregando(true);
        const timer = setTimeout(() => {
            api({ method: 'get', action: '/participante', token, params: parametrosBuscaFornecedor(termo), signal: controle.signal })
                .then((response) => {
                    setRegistros(Array.isArray(response?.data?.data) ? response.data.data : []);
                    setErro(null);
                    setAtivo(-1);
                    setCarregando(false);
                })
                .catch(() => {
                    if (controle.signal.aborted) return;
                    setRegistros([]);
                    setErro('Não foi possível buscar os fornecedores. Tente de novo.');
                    setCarregando(false);
                });
        }, buscaImediata ? 0 : ATRASO_BUSCA_MS);
        return () => {
            clearTimeout(timer);
            controle.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [termo, buscaImediata, token]);

    useEffect(() => {
        if (ativo < 0 || !listaRef.current) return;
        const linha = listaRef.current.querySelector(`[data-indice="${ativo}"]`);
        if (linha && linha.scrollIntoView) linha.scrollIntoView({ block: 'nearest' });
    }, [ativo]);

    const indisponivel = (registro) => (motivoIndisponivel ? motivoIndisponivel(fornecedorDoRegistro(registro)) : null);

    const selecionar = (registro) => {
        if (!registro || indisponivel(registro)) return;
        onSelecionar(fornecedorDoRegistro(registro));
    };

    const proximoDisponivel = (inicio, passo) => {
        for (let i = inicio; i >= 0 && i < registros.length; i += passo) {
            if (!indisponivel(registros[i])) return i;
        }
        return ativo;
    };

    // Enter nunca submete o orçamento: escolhe a linha destacada pelas setas ou busca na hora.
    const aoTeclarBusca = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setAtivo(proximoDisponivel(ativo + 1, 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setAtivo(proximoDisponivel(ativo - 1, -1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            if (ativo >= 0 && registros[ativo]) selecionar(registros[ativo]);
            else setBuscaImediata((n) => n + 1);
        }
    };

    const idAtual = fornecedorAtual?.id != null ? String(fornecedorAtual.id) : null;
    const podeRemover = !!onRemover && temFornecedor(fornecedorAtual);

    return ReactDOM.createPortal(
        // stopPropagation: o portal ainda borbulha pela árvore React até a grade, que rouba o foco no
        // mousedown e rola com as setas.
        <div className="modal fade show d-block fin-pp fin-pp__modalbg" data-modal-busca-fornecedor
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); if (e.target === e.currentTarget) onFechar(); }}>
            <div className="modal-dialog modal-dialog-centered modal-xl">
                <div className="modal-content fin-pp__modal fin-pp__modal--scroll">
                    <div className="fin-pp__modal-head">
                        <span className="fin-pp__modal-head-ic"><FaBuilding size={17} /></span>
                        <h3 className="fin-pp__modal-title">Selecionar fornecedor
                            <span className="fin-pp__modal-sub">{subtitulo || `Atual: ${rotuloFornecedor(fornecedorAtual)}`}</span>
                        </h3>
                        <button type="button" className="fin-pp__modal-close" title="Fechar (Esc)" onClick={onFechar}><FaTimes size={16} /></button>
                    </div>
                    <div className="modal-body">
                        <div className="fin-pp__search-field" style={{ marginBottom: 12 }}>
                            <FaSearch className="fin-pp__search-ic" size={14} />
                            <input
                                ref={inputRef}
                                type="text"
                                className="fin-pp__input"
                                placeholder="Buscar por nome fantasia, razão social, CNPJ ou código"
                                value={termo}
                                onChange={(e) => setTermo(e.target.value)}
                                onKeyDown={aoTeclarBusca}
                                data-busca-fornecedor
                                autoComplete="off"
                            />
                        </div>
                        <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                            <div className="fin-pp__tblwrap" ref={listaRef}>
                                <table className="fin-pp__tbl" style={{ minWidth: 820, tableLayout: 'auto' }}>
                                    <thead><tr>
                                        <th style={{ width: 80 }}>Código</th>
                                        <th>Nome fantasia</th>
                                        <th>Razão social</th>
                                        <th style={{ width: 170 }}>CNPJ / CPF</th>
                                        <th style={{ width: 170 }}>Cidade/UF</th>
                                    </tr></thead>
                                    <tbody>
                                        {carregando && registros.length === 0
                                            ? <tr><td colSpan={5} className="fin-pp__empty">Buscando…</td></tr>
                                            : erro
                                            ? <tr><td colSpan={5} className="fin-pp__empty">{erro}</td></tr>
                                            : registros.length === 0
                                            ? <tr><td colSpan={5} className="fin-pp__empty">Nenhum fornecedor encontrado</td></tr>
                                            : registros.map((registro, indice) => {
                                                const motivo = indisponivel(registro);
                                                const classes = [
                                                    String(registro.id) === idAtual ? 'is-selected' : '',
                                                    indice === ativo ? 'is-selecionada' : '',
                                                ].filter(Boolean).join(' ');
                                                return (
                                                    <tr key={registro.id} data-indice={indice} data-fornecedor-opcao={registro.id}
                                                        className={classes || undefined}
                                                        title={motivo || 'Selecionar este fornecedor'}
                                                        style={{ cursor: motivo ? 'not-allowed' : 'pointer', opacity: motivo ? 0.5 : 1 }}
                                                        onMouseEnter={() => { if (!motivo) setAtivo(indice); }}
                                                        onClick={() => selecionar(registro)}>
                                                        <td className="fin-pp__cell-muted">{registro.codigo ?? ''}</td>
                                                        <td className="fin-pp__cell-strong">
                                                            {registro.nomefantasia || <span className="fin-pp__cell-muted">—</span>}
                                                            {motivo && <span className="fin-pp__pill is-muted" style={{ marginLeft: 6 }}>Indisponível</span>}
                                                        </td>
                                                        <td>{registro.nome}</td>
                                                        <td className="fin-pp__cell-muted">{documentoFornecedor(registro)}</td>
                                                        <td className="fin-pp__cell-muted">{cidadeUfFornecedor(registro)}</td>
                                                    </tr>
                                                );
                                            })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <small className="d-block mt-2" style={{ color: 'var(--fin-ft)' }}>
                            {registros.length >= LIMITE_BUSCA_FORNECEDOR
                                ? `Mostrando os ${LIMITE_BUSCA_FORNECEDOR} primeiros — refine a busca para achar outro fornecedor.`
                                : 'Clique na linha para selecionar. ↑/↓ e Enter também escolhem; Esc fecha.'}
                        </small>
                    </div>
                    <div className="fin-pp__modal-foot">
                        {podeRemover && (
                            <button type="button" className="fin-pp__btn fin-pp__btn--danger-ghost" data-remover-fornecedor
                                style={{ marginRight: 'auto' }} onClick={onRemover}>
                                <FaTrash size={12} /> Remover fornecedor
                            </button>
                        )}
                        <button type="button" className="fin-pp__btn" onClick={onFechar}>Cancelar</button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
